import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {ObjectType} from 'app-core/framework/CoreObjectConstants';
import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {capitalize, typeToName} from 'util/NameUtil';
import {JSONValue, Override} from 'util/Types';

import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import EnumEditor from '../EnumEditor';
import StepConfigEditorRouter from '../StepConfigEditorRouter';
import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import DomainTypeSchemaSelector, {isDomainDefinition} from './DomainTypeSchemaSelector';

const VARIABLE_DEFINITION_CONFIG_SHAPE = PIPELINE_STEP_SHAPES[PipelineStepType.DEFINE_TEMPLATE_VARIABLES][0]
  .config.variables.elementSpec.fields.definition as GetPipelineConfigSpecType<'taggedUnion'>;

// The first choice (index 0) is 'array', which wraps the actual definition types
const ARRAY_CHOICE = VARIABLE_DEFINITION_CONFIG_SHAPE.choices[0];
const ELEMENT_SPEC_CONFIG_SHAPE = ARRAY_CHOICE.fields.elementSpec as GetPipelineConfigSpecType<'taggedUnion'>;

const DOMAIN_CHOICE_SHAPE = ELEMENT_SPEC_CONFIG_SHAPE.choices.find((choice) => {
  const kindField = choice.fields.kind as GetPipelineConfigSpecType<'string'>;
  return kindField.value === 'domain';
}) as GetPipelineConfigSpecType<'shape'>;

const DOMAIN_TYPES_ARRAY_SPEC = DOMAIN_CHOICE_SHAPE.fields.types as GetPipelineConfigSpecType<'array'>;
const DOMAIN_TYPES_ENUM_SPEC = DOMAIN_TYPES_ARRAY_SPEC.elementSpec as GetPipelineConfigSpecType<'enum'>;
const DOMAIN_TYPE_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  (DOMAIN_TYPES_ENUM_SPEC.choices as ReadonlyArray<string>).map((type) => [
    type,
    capitalize(typeToName(getModelTypeConstantForObjectType(type as ObjectType))),
  ])
);

const TYPE_DISPLAY_NAMES: Record<string, string> = {
  boolean: 'Boolean',
  integer: 'Integer',
  number: 'Decimal',
  string: 'Text',
  domain: 'Benchling type',
};

const TYPE_CHOICES = ELEMENT_SPEC_CONFIG_SHAPE.choices.map((choice) => {
  const kindField = choice.fields.kind as GetPipelineConfigSpecType<'string'>;
  return kindField.value as string;
});

const REQUIRED_OPTIONS = [
  {name: 'Yes', value: true},
  {name: 'No', value: false},
];

const DEFAULT_VARIABLE: VariableConfig = {
  name: '',
  definition: {kind: 'boolean', required: true},
  value: null,
};

export type VariableConfig = PipelineStepConfig<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0,
  'draft'
>['variables'][number];

type VariableDefinition = VariableConfig['definition'];

function convertDefinitionToArray(
  definition: VariableDefinition
): Extract<VariableDefinition, {kind: 'array'}> {
  return definition?.kind === 'array'
    ? definition
    : {kind: 'array', required: true, elementSpec: definition ?? {kind: 'boolean', required: true}};
}

function convertDefinitionFromArray(
  definition: VariableDefinition
): Exclude<VariableDefinition, {kind: 'array'}> {
  return definition?.kind === 'array' ? definition?.elementSpec : definition;
}

export type NonArrayDefinition = Exclude<VariableConfig['definition'], {kind: 'array'}>;

interface VariableDefinitionEditorProps {
  config: NonArrayDefinition;
  onChangeConfig: (config: NonArrayDefinition) => void;
  disabled?: boolean;
}

// minValue/maxValue only exist on 'integer' type, not 'number' per the YAML schema
function getDefaultConfigForType(kind: string): NonArrayDefinition {
  switch (kind) {
    case 'boolean':
      return {kind: 'boolean', required: true};
    case 'integer':
      return {kind: 'integer', required: true, minValue: null, maxValue: null};
    case 'number':
      return {kind: 'number', required: true};
    case 'string':
      return {kind: 'string', required: true};
    case 'domain':
      return {kind: 'domain', required: true, types: ['PLATE'], schemaIds: null, schemaInterfaceIds: null};
    default:
      return {kind: 'boolean', required: true};
  }
}

/**
 * Editor to configure the variable's definition.
 */
function VariableDefinitionEditor({
  config,
  onChangeConfig,
  disabled,
}: VariableDefinitionEditorProps): JSX.Element {
  const selectedType = config?.kind ?? null;
  const isDomain = selectedType === 'domain';

  return (
    <div className={css(styles.definitionEditor)}>
      <div
        className={css(styles.fieldRow)}
        {...getTestProps({component: 'VariableDefinitionEditor', element: 'type-field'})}
      >
        <VariableHeaderOrNull variableName="Type" isCompact={false} />
        <DropdownSelector<string>
          selectedValue={selectedType}
          onChange={(newType) => {
            if (newType != null) {
              onChangeConfig(getDefaultConfigForType(newType));
            }
          }}
          allowNull={false}
          buttonSize={ButtonSize.SMALL}
          searchable={false}
          optionDatas={TYPE_CHOICES.map((type) => ({
            name: TYPE_DISPLAY_NAMES[type] ?? type,
            value: type,
          }))}
          disabled={disabled}
        />
      </div>

      {isDomain && isDomainDefinition(config) && (
        <div className={css(styles.fieldRow)}>
          <VariableHeaderOrNull variableName="Benchling type" isCompact={false} />
          <EnumEditor
            config={config.types[0] ?? null}
            configShape={DOMAIN_TYPES_ENUM_SPEC}
            onChangeConfig={(newType) => {
              if (newType != null) {
                onChangeConfig({
                  ...config,
                  types: [newType as (typeof config.types)[number]],
                  schemaIds: null,
                  schemaInterfaceIds: null,
                });
              }
            }}
            uiSchema={{displayNames: DOMAIN_TYPE_DISPLAY_NAMES}}
            disabled={disabled}
            buttonSize={ButtonSize.SMALL}
          />
        </div>
      )}

      {isDomain && isDomainDefinition(config) && (
        <DomainTypeSchemaSelector config={config} onChangeConfig={onChangeConfig} disabled={disabled} />
      )}

      {config?.kind === 'integer' && (
        <>
          <div className={css(styles.fieldRow)}>
            <VariableHeaderOrNull variableName="Minimum value" isCompact={false} />
            <IntermediateValueControlledNumericInput
              value={config.minValue}
              onChange={(e) => {
                onChangeConfig({...config, minValue: e.target.numericValue});
              }}
              placeholder="No minimum"
              isDisabled={disabled}
              validateInteger={true}
            />
          </div>
          <div className={css(styles.fieldRow)}>
            <VariableHeaderOrNull variableName="Maximum value" isCompact={false} />
            <IntermediateValueControlledNumericInput
              value={config.maxValue}
              onChange={(e) => {
                onChangeConfig({...config, maxValue: e.target.numericValue});
              }}
              placeholder="No maximum"
              isDisabled={disabled}
              validateInteger={true}
            />
          </div>
        </>
      )}

      {selectedType != null && (
        <div
          className={css(styles.fieldRow)}
          {...getTestProps({component: 'VariableDefinitionEditor', element: 'required-field'})}
        >
          <VariableHeaderOrNull variableName="Required" isCompact={false} />
          <DropdownSelector<boolean>
            selectedValue={config?.required ?? null}
            onChange={(newRequired) => {
              if (config != null) {
                onChangeConfig({...config, required: newRequired ?? true});
              }
            }}
            allowNull={false}
            buttonSize={ButtonSize.SMALL}
            searchable={false}
            optionDatas={REQUIRED_OPTIONS}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

interface VariableValueEditorProps {
  definition: VariableConfig['definition'];
  config: VariableConfig['value'];
  onChangeConfig: (config: VariableConfig['value']) => void;
  disabled?: boolean;
}

/**
 * Editor to configure the variable's value.
 */
function VariableValueEditor({
  definition,
  config,
  onChangeConfig,
  disabled,
}: VariableValueEditorProps): JSX.Element {
  if (definition?.kind == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="Select a variable type." />;
  }
  if (definition?.required == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="Select if the variable is required." />;
  }

  return (
    <>
      <StepConfigEditorRouter
        config={{value: config}}
        configShape={{kind: 'shape', fields: {value: definition as PipelineConfigJsonSpec}, required: true}}
        onChangeConfig={({value}: {value: JSONValue}) => {
          onChangeConfig(value);
        }}
        uiSchema={{value: {displayName: 'Value'}}}
        isTopLevel={true}
        disabled={disabled}
        dataTestAttrs={{}}
      />
      <div>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="The value above is replaced each time the template is applied."
        />
      </div>
    </>
  );
}

interface VariableEditorProps {
  index: number;
  config: VariableConfig;
  onChangeConfig: (config: VariableConfig) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

/**
 * Editor to configure a variable's name, definition and value.
 *
 *  `VariableEditor` uses multiple
 * @package
 */
export function VariableEditor({
  index,
  config,
  onChangeConfig,
  onRemove,
  canRemove,
  disabled,
}: VariableEditorProps): JSX.Element {
  return (
    <div className={css(styles.variableEditor)}>
      <Row>
        <Col sm={6}>
          <VariableHeaderOrNull variableName="Name" isCompact={false} />
          <TextInput
            value={config.name ?? ''}
            placeholder="Name"
            onChange={(e) => {
              onChangeConfig({...config, name: e.target.value});
            }}
            disabled={disabled}
          />
        </Col>
        <Col sm={5}>
          <VariableHeaderOrNull variableName="Multi-select" isCompact={false} />
          <CheckboxInput
            id={`variable-is-multi-select-${index}`}
            label="Allow multiple values"
            checked={config.definition?.kind === 'array'}
            onChange={(e) => {
              if (e.target.checked) {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionToArray(config.definition),
                  value: [config.value],
                });
              } else {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionFromArray(config.definition),
                  value: Array.isArray(config.value) && config.value.length > 0 ? config.value[0] : null,
                });
              }
            }}
          />
        </Col>
        <Col sm={1}>
          <div className={css(styles.removeIcon)}>
            <Tooltip text={canRemove ? 'Remove variable' : 'Cannot remove the last variable'}>
              <DeleteIcon icon="trash-can" disabled={!canRemove || disabled} onClick={onRemove} />
            </Tooltip>
          </div>
        </Col>
      </Row>
      <VariableDefinitionEditor
        config={convertDefinitionFromArray(config.definition)}
        onChangeConfig={(definition) => {
          onChangeConfig({
            ...config,
            ...(config.definition?.kind === 'array'
              ? {definition: convertDefinitionToArray(definition), value: []}
              : {definition, value: null}),
          });
        }}
        disabled={disabled}
      />
      <VariableValueEditor
        definition={config.definition}
        config={config.value}
        onChangeConfig={(value) => {
          onChangeConfig({...config, value});
        }}
        disabled={disabled}
      />
    </div>
  );
}

type DefineTemplateVariablesEditorProps = Override<
  CustomComponentEditorProps,
  {config: PipelineStepConfig<PipelineStepType.DEFINE_TEMPLATE_VARIABLES, 0, 'draft'>}
>;

/**
 * Custom editor for `DEFINE_TEMPLATE_VARIABLES` steps.
 *
 * In `DEFINE_TEMPLATE_VARIABLES` steps, each variable has a definition (`config.variables[0].definition`),
 * and a value which conforms to that definition (`config.variables[0].value`).
 *
 * Definitions' format is a narrowed version of `PipelineConfigJsonSpec`.
 *
 * A custom editor is used, so variables' values can be updated dynamically with their definition.
 */
export default function DefineTemplateVariablesEditor({
  config,
  onChangeConfig,
  disabled,
}: DefineTemplateVariablesEditorProps): JSX.Element {
  const variables = config.variables ?? [];

  return (
    <>
      {variables.map((variable, index) => (
        <VariableEditor
          key={`variable-editor-${index}`}
          index={index}
          config={variable}
          onChangeConfig={(variable) => {
            onChangeConfig(config, ['variables'], config.variables.toSpliced(index, 1, variable));
          }}
          onRemove={() => {
            onChangeConfig(config, ['variables'], immutableRemoveAt(variables, index));
          }}
          canRemove={variables.length > 1}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <div className="u-moderate-spacing-top">
          <AddItemButton
            onClick={() => {
              onChangeConfig(config, ['variables'], [...variables, {...DEFAULT_VARIABLE}]);
            }}
            label="Add variable"
          />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  removeIcon: {
    float: 'right',
  },
  variableEditor: {
    background: GRAY_LIGHTER,
    padding: 10,
    marginBottom: 10,
  },
  definitionEditor: {
    marginTop: 8,
  },
  fieldRow: {
    marginBottom: 8,
  },
});
